import { Check } from 'lucide-react';

interface PresetOption {
  value: string;
  label: string;
}

interface PresetSelectorProps {
  id?: string;
  label: string;
  description?: string;
  value: string;
  options: PresetOption[];
  onChange: (value: string) => void;
  readOnly?: boolean;
  previewType?: 'shadow' | 'spacing' | 'default';
}

export function PresetSelector({
  label,
  description,
  value,
  options,
  onChange,
  readOnly = false,
  previewType = 'default',
}: PresetSelectorProps) {
  return (
    <div className="token-input space-y-2">
      {/* Label */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* Preset Options */}
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !readOnly && onChange(option.value)}
              disabled={readOnly}
              className={`relative px-4 py-3 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {/* Check icon */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="w-4 h-4 text-primary-600" />
                </div>
              )}

              {/* Label */}
              <div className="text-sm font-medium text-gray-900 mb-1">
                {option.label}
              </div>

              {/* Preview */}
              {previewType === 'shadow' && (
                <div
                  className="w-full h-8 bg-white rounded border border-gray-200"
                  style={{ boxShadow: option.value === 'none' ? 'none' : option.value }}
                />
              )}

              {previewType === 'spacing' && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full" />
                  <div
                    className="h-0.5 bg-primary-300"
                    style={{ width: option.value }}
                  />
                  <div className="w-2 h-2 bg-primary-500 rounded-full" />
                </div>
              )}

              {/* Value */}
              <div className="text-xs text-gray-500 font-mono mt-1">
                {option.value}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
